import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { ResponseDocumentSubtype, SCOPE_DCE, SCOPE_INTERNAL_DOC } from '../../models/response-document-subtype';
import { ApiResponseProjectsService } from '../../services/api/response-projects/api-response-projects.service';
import { TendersResponsesService } from '../../services/tenders-responses.service';
import { DEFAULT_PROJECT_NAME, ResponseProject, ResponseProjetAiMessageScenario } from '../../models/response-project';
import { ResponseProjectUploadFile } from '../../models/response-project-document';
import { UploaderChangeEvent } from '../../models/uploader-change-event';
import { ResponseModuleStateService } from '../../services/response-module-state.service';
import { sortInternalDocumentSubtypes } from '../documents/document-types';

@Component({
  selector: 'app-response-project-wizard',
  templateUrl: './response-project-wizard.component.html',
  styleUrls: ['./response-project-wizard.component.scss'],
})
export class ResponseProjectWizardComponent implements OnChanges, OnDestroy {
  @Input() documentSubtypes: ResponseDocumentSubtype[] = [];

  files: ResponseProjectUploadFile[] = [];
  internalFiles: ResponseProjectUploadFile[] = [];
  project: ResponseProject | null = null;
  dceSubtypeOptions: ResponseDocumentSubtype[] = [];
  internalSubtypeOptions: ResponseDocumentSubtype[] = [];

  get projectId(): string | null {
    return this.project?.id ?? null;
  }

  aiScenario: ResponseProjetAiMessageScenario = ResponseProjetAiMessageScenario.WELCOME;
  aiFileCount = 0;
  addedPhraseIndex = 0;
  removedPhraseIndex = 0;
  showInternalUploader = false;
  internalDismissed = false;
  internalContinueClicked = false;
  panelsCollapsed = false;
  private processingStarted = false;
  private askInternalTimer: ReturnType<typeof setTimeout> | null = null;
  private finalizingTimer: ReturnType<typeof setTimeout> | null = null;
  private waitingForInternal = false;

  constructor(
    private apiResponseProjects: ApiResponseProjectsService,
    private tendersResponses: TendersResponsesService,
    private router: Router,
    private responseModuleStateService: ResponseModuleStateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentSubtypes']) {
      this.dceSubtypeOptions = this.buildSubtypeOptions(SCOPE_DCE);
      this.internalSubtypeOptions = this.buildSubtypeOptions(SCOPE_INTERNAL_DOC);
    }
  }

  get defaultDceSubtypeId(): number | undefined {
    return this.defaultSubtypeId(SCOPE_DCE);
  }
  get defaultInternalSubtypeId(): number | undefined {
    return this.defaultSubtypeId(SCOPE_INTERNAL_DOC);
  }

  private defaultSubtypeId(scope: string): number | undefined {
    return this.documentSubtypes.find(s => s.name === 'undefined' && s.scope === scope)?.id;
  }

  private buildSubtypeOptions(scope: string): ResponseDocumentSubtype[] {
    const options = this.documentSubtypes.filter(s => s.name !== 'undefined' && s.scope === scope);
    if (scope === SCOPE_INTERNAL_DOC) return sortInternalDocumentSubtypes(options);
    return options.sort((a, b) => a.displayedName.localeCompare(b.displayedName, undefined, { sensitivity: 'base' }));
  }
  onInternalUploaderChange({ files, scenario }: UploaderChangeEvent): void {
    const prevCount = this.internalFiles.length;
    this.internalFiles = files;
    const nextCount = files.length;

    if (prevCount === 0 && nextCount > 0 && this.waitingForInternal) {
      this.waitingForInternal = false;
      this.clearTimer('askInternalTimer');
    }

    if (scenario !== null) {
      if (scenario === ResponseProjetAiMessageScenario.READY) {
        this.aiScenario = ResponseProjetAiMessageScenario.FINALIZING;
        this.finalizingTimer = setTimeout(() => {
          this.finalizingTimer = null;
          this.aiScenario = ResponseProjetAiMessageScenario.READY;
        }, 1500);
        return;
      }
      this.aiScenario = scenario;
      return;
    }

    this.aiFileCount = nextCount;
    if (nextCount === 0 && prevCount > 0) {
      this.internalContinueClicked = false;
      this.aiScenario = ResponseProjetAiMessageScenario.ASK_INTERNAL;
      return;
    }
    this.applyFileCountScenario(prevCount, nextCount, this.internalContinueClicked);
  }

  onDceUploaderChange({ files, scenario }: UploaderChangeEvent): void {
    const prevCount = this.files.length;
    this.files = files;
    const nextCount = files.length;

    if (!this.showInternalUploader || nextCount === 0) {
      this.aiFileCount = nextCount;
    }

    if (scenario !== null) {
      if (scenario === ResponseProjetAiMessageScenario.CATEGORIZING) {
        this.showInternalUploader = true;
        this.waitingForInternal = true;
        this.aiScenario = scenario;
        this.askInternalTimer = setTimeout(() => {
          this.askInternalTimer = null;
          this.aiScenario = ResponseProjetAiMessageScenario.ASK_INTERNAL;
        }, 3000);
        return;
      }
      // WORKING/READY du DCE n'écrasent pas les messages de l'uploader interne
      if (scenario !== ResponseProjetAiMessageScenario.WORKING && scenario !== ResponseProjetAiMessageScenario.READY) {
        this.aiScenario = scenario;
      }
      return;
    }

    this.applyFileCountScenario(prevCount, nextCount, this.processingStarted, () => this.resetWizard());
  }

  private applyFileCountScenario(prevCount: number, nextCount: number, processingStarted: boolean, onEmpty?: () => void): void {
    if (nextCount > prevCount && !processingStarted) {
      this.aiScenario = prevCount === 0 ? ResponseProjetAiMessageScenario.RECEIVED : ResponseProjetAiMessageScenario.ADDED;
      if (prevCount > 0) this.addedPhraseIndex = (this.addedPhraseIndex + 1) % 3;
    } else if (nextCount < prevCount) {
      if (nextCount === 0) {
        onEmpty?.();
      } else if (!processingStarted) {
        this.aiScenario = ResponseProjetAiMessageScenario.REMOVED;
        this.removedPhraseIndex = (this.removedPhraseIndex + 1) % 3;
      }
    }
  }

  private resetWizard(): void {
    this.aiScenario = ResponseProjetAiMessageScenario.WELCOME;
    this.processingStarted = false;
    this.showInternalUploader = false;
    this.internalDismissed = false;
    this.internalContinueClicked = false;
    this.panelsCollapsed = false;
    this.waitingForInternal = false;
    this.clearTimer('askInternalTimer');
    this.clearTimer('finalizingTimer');
    this.files = [];
    this.internalFiles = [];
    this.tendersResponses.setFiles('new', SCOPE_DCE, []);
    this.tendersResponses.setFiles('new', SCOPE_INTERNAL_DOC, []);
    if (this.project) {
      this.apiResponseProjects.deleteResponseProject(this.project.id).subscribe({ error: () => {} });
      this.tendersResponses.removeProject(this.project.id);
      this.project = null;
    }
  }

  ngOnDestroy(): void {
    this.clearTimer('askInternalTimer');
    this.clearTimer('finalizingTimer');
    this.tendersResponses.setFiles('new', SCOPE_DCE, []);
    this.tendersResponses.setFiles('new', SCOPE_INTERNAL_DOC, []);
    if (this.project) {
      this.tendersResponses.setFiles(this.project.id, SCOPE_DCE, []);
      this.tendersResponses.setFiles(this.project.id, SCOPE_INTERNAL_DOC, []);
      this.apiResponseProjects.deleteResponseProject(this.project.id).subscribe({ error: () => {} });
      this.tendersResponses.removeProject(this.project.id);
    }
  }

  private clearTimer(key: 'askInternalTimer' | 'finalizingTimer'): void {
    if (this[key]) {
      clearTimeout(this[key]!);
      this[key] = null;
    }
  }

  onContinueClicked(): void {
    if (this.projectId) return;
    this.processingStarted = true;

    this.apiResponseProjects.createResponseProject({ name: DEFAULT_PROJECT_NAME }).subscribe({
      next: project => {
        this.project = project;
      },
      error: () => {},
    });
  }

  get showWordCard(): boolean {
    return this.showInternalUploader && (this.internalDismissed || this.internalContinueClicked);
  }

  onInternalContinueClicked(): void {
    this.internalContinueClicked = true;
  }

  get showInternalLaterLink(): boolean {
    return this.showInternalUploader && this.internalFiles.length === 0 && !this.internalDismissed;
  }

  onInternalLaterClick(): void {
    this.internalDismissed = true;
    this.panelsCollapsed = true;
  }

  onProjectNameChange(name: string): void {
    if (this.project) this.project = { ...this.project, name };
  }

  onLaterClick(): void {
    if (this.project) {
      this.responseModuleStateService.refresh().subscribe();
      this.tendersResponses.addProject(this.project);
      void this.router.navigate(['/tenders/responses', this.project.id]);
      this.project = null;
    }
  }
}
